import { Box, Divider, Typography } from "@mui/material";
import { observer } from "mobx-react-lite";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import SuccessIcon from "../../../../assets/common/icons/success-icon.svg";
import BriaButton from "../../../../components/common/BriaButton/BriaButton";
import BriaRadioButton from "../../../../components/common/BriaRadioButton/BriaRadioButton";
import BriaTooltip from "../../../../components/common/BriaTooltip/BriaTooltip";
import ConfirmationPopup from "../../../../components/common/ConfirmationPopup/ConfirmationPopup";
import ImageUploader from "../../../../components/common/ImageUploader/ImageUploader";
import { useAppStore } from "../../../../hooks/useStores";
import InputLayout from "../../../../layout/InputLayout/InputLayout";
import { TgDatasetCaptionSource } from "../../../../pages/NewTG/Datasets/store/tg-dataset-store.tsx";
import { canTgProjectGeneratePrefix } from "../../../../pages/NewTG/utils/tg-utils.ts";
import BriaInput from "../../../common/BriaInput/BriaInput.tsx";
import LoadingPlaceholder from "../../../common/LoadingPlaceholder/LoadingPlaceholder.tsx";
import ZipFileUploader from "../../../common/ZipFileUploader/ZipFileUploader.tsx";
import styles from "./TgAdvancedImageUploadSetup.module.scss";
import { useAdvancedUploadValidation } from "./hooks/useAdvancedUploadValidation";

type TgAdvancedImageUploadSetupProps = {
	onComplete?: (initialStatus?: any) => void;
};

type UploadMethod = "zip" | "url";

const MAX_PREFIX_IMAGES = 6;
const MIN_PREFIX_IMAGES = 3;
const MAX_PREFIX_IMAGES_SIZE_MB = 12;

const MAX_ZIP_SIZE_GB = 5;

const TgAdvancedImageUploadSetup = ({ onComplete }: TgAdvancedImageUploadSetupProps) => {
	const { t } = useTranslation("tg", { keyPrefix: "models.create.advancedUpload" });
	const { tgStore, uiStore } = useAppStore();
	const { tgProjectStore, tgDatasetStore } = tgStore;
	const { validateZipBlob, getValidationErrorMessage } = useAdvancedUploadValidation();

	const [initialPrefix, setInitialPrefix] = useState("");
	const [uploadMethod, setUploadMethod] = useState<UploadMethod>("zip");
	const [captionMethod, setCaptionMethod] = useState<TgDatasetCaptionSource>("automatic");
	const [zipUrl, setZipUrl] = useState("");
	const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
	const [zipUploadProgress, setZipUploadProgress] = useState(0);
	const [isUploadingZipFile, setIsUploadingZipFile] = useState(false);
	const [isUploadingZipUrl, setIsUploadingZipUrl] = useState(false);
	const [urlUploadError, setUrlUploadError] = useState<string | null>(null);
	const [urlUploadSuccess, setUrlUploadSuccess] = useState(false);

	const [showPrefixPopup, setShowPrefixPopup] = useState(false);
	const [prefixImages, setPrefixImages] = useState<File[]>([]);
	const [generatingPrefix, setGeneratingPrefix] = useState(false);
	const [isCreatingDataset, setIsCreatingDataset] = useState(true);
	const [isUploadingDataset, setIsUploadingDataset] = useState(false);

	useEffect(() => {
		const createInitialDataset = async () => {
			try {
				setIsCreatingDataset(true);
				const payload = {
					...tgDatasetStore.datasetForm,
					upload_type: "advanced" as const,
					project_id: tgProjectStore.projectToView.id,
				};

				tgDatasetStore.datasetForm = await tgDatasetStore.createDataset(payload as any);
				setInitialPrefix(tgDatasetStore.datasetForm.caption_prefix);
			} catch (e: any) {
				if (e?.response?.data?.message) {
					uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
				}
			} finally {
				setIsCreatingDataset(false);
			}
		};

		if (tgProjectStore.projectToView.id && !tgDatasetStore.datasetForm.id) {
			createInitialDataset();
		} else {
			setIsCreatingDataset(false);
		}

		return () => {
			if (tgDatasetStore.datasetForm.id && !tgDatasetStore.datasetForm.name) {
				tgDatasetStore.deleteDataset(tgDatasetStore.datasetForm.id);
			}
		};
	}, []);

	const handleGeneratePrefix = async () => {
		if (!canTgProjectGeneratePrefix(tgProjectStore.projectToView.ip_type)) {
			return;
		}

		if (prefixImages.length < 3 || prefixImages.length > 6) {
			uiStore.showSnackBarAlert("error", t("prefixImagesCountError"), 5000);
			return;
		}

		setGeneratingPrefix(true);
		try {
			const response = await tgDatasetStore.imagesSampleGeneratePrefix(tgProjectStore.projectToView, {
				images: prefixImages,
			});
			if (!response || !response?.prefix) throw new Error();

			const newPrefix = response.prefix;
			if (newPrefix) {
				tgDatasetStore.handleFormChange("caption_prefix", newPrefix);
			}
			setShowPrefixPopup(false);
		} catch (error) {
			uiStore.showSnackBarAlert("error", t("prefixGenerationError"), 5000);
		} finally {
			setGeneratingPrefix(false);
		}
	};

	const handleResetPrefix = () => {
		tgDatasetStore.handleFormChange("caption_prefix", initialPrefix);
	};

	const resetUrlState = () => {
		setZipUrl("");
		setUrlUploadError(null);
		setUrlUploadSuccess(false);
	};

	const resetFileState = () => {
		setUploadedFiles([]);
		setZipUploadProgress(0);
	};

	const startFileUpload = (file: File) => {
		setIsUploadingZipFile(true);
		setZipUploadProgress(0);
		setUploadedFiles([file]);
		// exclusivity: clear URL path
		resetUrlState();
	};

	const startUrlUpload = () => {
		setIsUploadingZipUrl(true);
		setUrlUploadError(null);
		setUrlUploadSuccess(false);
		// exclusivity: clear File path
		resetFileState();
	};

	const validateZipForFile = async (blob: Blob): Promise<boolean> => {
		const validation = await validateZipBlob(blob);
		if (!validation.ok) {
			const message = getValidationErrorMessage(validation);
			uiStore.showErrorAlertWithCode(400, message!);
			return false;
		}
		return true;
	};

	const validateZipForUrl = async (blob: Blob): Promise<boolean> => {
		const validation = await validateZipBlob(blob);
		if (!validation.ok) {
			const message = getValidationErrorMessage(validation);
			setUrlUploadError(message);
			setUrlUploadSuccess(false);
			return false;
		}
		return true;
	};

	const handleUploadZip = async (zipFile: File) => {
		startFileUpload(zipFile);
		try {
			const valid = await validateZipForFile(zipFile);
			if (!valid) {
				resetFileState();
				return;
			}
			const zipName = `dataset_zip_images_${Date.now()}.zip`;
			await tgDatasetStore.uploadZipSimple(zipFile, (pct) => setZipUploadProgress(pct), zipName);
		} catch (e: any) {
			if (e?.response?.data?.message) {
				uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
			}
		} finally {
			setIsUploadingZipFile(false);
		}
	};

	const handleUploadUrl = async () => {
		if (!zipUrl.trim()) {
			setUrlUploadError(t("urlRequired"));
			return;
		}

		startUrlUpload();

		try {
			const resp = await fetch(zipUrl, { method: "GET" });
			if (!resp.ok) throw new Error("fetch_failed");
			const blob = await resp.blob();
			const valid = await validateZipForUrl(blob);
			if (!valid) return;
			const fileName = `dataset_zip_images_${Date.now()}.zip`;
			const file = new File([blob], fileName, { type: blob.type || "application/zip" });
			await tgDatasetStore.uploadZipSimple(file);
			setUrlUploadSuccess(true);
		} catch (error) {
			const isCors = error instanceof TypeError; // common fetch CORS/network error in browsers
			setUrlUploadError(isCors ? t("errors.url.corsBlocked") : t("errors.url.fetchFailed"));
		} finally {
			setIsUploadingZipUrl(false);
		}
	};

	const handleRemoveFile = () => {
		setUploadedFiles([]);
		setZipUploadProgress(0);
		tgDatasetStore.imagesZipUrl = undefined;
	};

	const handleRemoveUrl = () => {
		setZipUrl("");
		setUrlUploadSuccess(false);
		setUrlUploadError(null);
		tgDatasetStore.imagesZipUrl = undefined;
	};

	const getSectionTitle = () => {
		return tgDatasetStore.datasetForm.captions_source === "automatic"
			? t("selectImageSource")
			: t("selectSourceBoth");
	};

	const getSectionDescription = () => {
		return tgDatasetStore.datasetForm.captions_source === "manual" ? t("selectSourceBothDescription") : "";
	};

	const handleUploadDataSet = async () => {
		try {
			if (!tgDatasetStore.datasetForm.id) {
				// Incase create dataset failed
				const payload = {
					...tgDatasetStore.datasetForm,
					upload_type: "advanced" as const,
					project_id: tgProjectStore.projectToView.id,
				};

				tgDatasetStore.datasetForm = await tgDatasetStore.createDataset(payload as any);
			}
			setIsUploadingDataset(true);

			const payload = {
				...tgDatasetStore.datasetForm,
			};

			// Have to include before update request since update request override this value
			const captions_source = tgDatasetStore.datasetForm.captions_source ?? "automatic";

			tgDatasetStore.datasetForm = await tgDatasetStore.updateDataset(
				tgDatasetStore.datasetForm.id,
				payload as any,
			);

			tgDatasetStore.datasetForm.captions_source = captions_source;

			await tgDatasetStore.startBulkImagesUpload(
				tgDatasetStore.datasetForm.id,
				tgDatasetStore.imagesZipUrl || "",
				captions_source === "automatic",
			);

			// Fetch initial status once (AdvancedImageLoading will poll thereafter)
			const initialStatus = await tgDatasetStore.getBulkImagesUploadStatus(tgDatasetStore.datasetForm.id);

			onComplete?.(initialStatus as any);
		} catch (e: any) {
			if (e?.response?.data?.message) {
				uiStore.showErrorAlertWithCode(e?.response?.status, e?.response?.data?.message);
			}
		} finally {
			setIsUploadingDataset(false);
		}
	};

	const handleCaptionClick = (value: any) => {
		if (value != captionMethod) {
			// Reset files
			setUploadedFiles([]);
			setZipUrl("");
			setUrlUploadError(null);
			setUrlUploadSuccess(false);
			tgDatasetStore.imagesZipUrl = undefined;
		}
		setCaptionMethod(value);
		tgDatasetStore.handleFormChange("captions_source", value as any);
	};

	return (
		<LoadingPlaceholder isLoading={isCreatingDataset} className={styles.loadingPlaceholder}>
			<Box className={styles.container}>
				{/* Dataset Name Section */}
				<InputLayout label={t("datasetName.label")} showLabel={true} labelSize={"extraLarge"}>
					<BriaInput
						value={tgDatasetStore.datasetForm.name}
						onChange={(e) => {
							tgDatasetStore.handleFormChange("name", e.target.value);
						}}
						placeholder={t("datasetName.placeholder")}
						className={styles.textField}
						height={"40px"}
					/>
				</InputLayout>

				<Divider className={styles.sectionDivider} />

				{/* Prefix Section */}
				<InputLayout
					label={t("prefix.label")}
					info={t("prefix.tooltip")}
					showLabel={true}
					labelSize={"extraLarge"}
				>
					<Typography className={styles.prefixDescription}>{t("prefix.description")}</Typography>
					<Box className={styles.prefixContainer}>
						<BriaInput
							value={tgDatasetStore.datasetForm.caption_prefix}
							onChange={(e) => {
								tgDatasetStore.handleFormChange("caption_prefix", e.target.value);
							}}
							placeholder={t("prefix.placeholder")}
							className={styles.prefixField}
							height={"40px"}
							InputProps={{
								style: {
									height: "40px",
								},
								endAdornment: (
									<BriaButton buttonType={"textMedium"} onClick={handleResetPrefix}>
										{t("resetPrefix")}
									</BriaButton>
								),
							}}
						/>
						<Box className={styles.prefixButtons}>
							<BriaTooltip
								title={t("prefixGenerationDisabled")}
								disable={canTgProjectGeneratePrefix(tgProjectStore.projectToView.ip_type)}
							>
								<BriaButton
									buttonType="secondaryMedium"
									onClick={() => setShowPrefixPopup(true)}
									className={styles.generateButton}
									disabled={!canTgProjectGeneratePrefix(tgProjectStore.projectToView.ip_type)}
								>
									{t("generatePrefix")}
								</BriaButton>
							</BriaTooltip>
						</Box>
					</Box>
				</InputLayout>

				<Divider className={styles.sectionDivider} />

				{/* Captions Section */}
				<Box className={styles.captionsContainer}>
					<InputLayout label={t("captions.label")} showLabel={true} labelSize={"extraLarge"}>
						<BriaRadioButton
							buttons={[
								{
									label: t("captions.automatic.label"),
									value: "automatic",
									disabled: false,
									subDescription: t("captions.automatic.description"),
								},
								{
									label: t("captions.manual.label"),
									value: "manual",
									disabled: false,
									subDescription: t("captions.manual.description"),
								},
							]}
							defaultValue={tgDatasetStore.datasetForm.captions_source ?? captionMethod}
							onClick={handleCaptionClick}
							spacing={500}
						/>
					</InputLayout>
				</Box>

				<Divider className={styles.sectionDivider} />

				{/* Select Source Section */}
				<InputLayout label={getSectionTitle()} showLabel={true} labelSize={"extraLarge"}>
					{getSectionDescription() && (
						<Typography className={styles.sectionDescription}>{getSectionDescription()}</Typography>
					)}

					<BriaRadioButton
						buttons={[
							{
								label: t("uploadMethod.zip"),
								value: "zip",
								disabled: isUploadingZipFile || isUploadingZipUrl,
							},
							{
								label: t("uploadMethod.url"),
								value: "url",
								disabled: isUploadingZipFile || isUploadingZipUrl,
							},
						]}
						defaultValue={uploadMethod}
						onClick={(value) => {
							setUploadMethod(value as UploadMethod);
						}}
					/>

					{uploadMethod === "zip" ? (
						<Box className={styles.zipUploadSection}>
							<ZipFileUploader
								onUpload={handleUploadZip}
								handleRemoveFile={handleRemoveFile}
								maxSizeGB={MAX_ZIP_SIZE_GB}
								showUploadedFiles={true}
								uploadedFiles={uploadedFiles}
								showProgress={true}
								uploadProgress={zipUploadProgress}
								isUploading={isUploadingZipFile}
							/>
						</Box>
					) : (
						<Box className={styles.urlUploadContainer}>
							<Typography className={styles.urlSectionTItle}>{t("urlUploadTitle")}</Typography>
							<Box className={styles.urlUploadSection}>
								<BriaInput
									value={zipUrl}
									onChange={(e) => setZipUrl(e.target.value)}
									placeholder={t("urlPlaceholder")}
									className={styles.urlField}
									height={"40px"}
								/>
								<BriaButton
									buttonType={"secondaryMedium"}
									onClick={urlUploadSuccess ? handleRemoveUrl : handleUploadUrl}
									loading={isUploadingZipUrl}
									disabled={isUploadingZipUrl || !zipUrl.trim()}
									className={styles.uploadUrlButton}
								>
									{urlUploadSuccess ? t("remove") : t("upload")}
								</BriaButton>
								{urlUploadSuccess && (
									<Box className={styles.urlSuccessMessage}>
										<img alt={"success"} src={SuccessIcon} />
										<Box className={styles.successTextContainer}>
											<Typography className={styles.successTitle}>
												{t("urlUploadComplete")}
											</Typography>
											<Typography className={styles.successDescription}>
												{t("urlUploadDescription")}
											</Typography>
										</Box>
									</Box>
								)}
								{urlUploadError && (
									<Box className={styles.urlErrorMessage}>
										<Typography className={styles.errorIcon}>✕</Typography>
										<Box className={styles.errorTextContainer}>
											<Typography className={styles.errorTitle}>{t("urlUploadError")}</Typography>
											<Typography className={styles.errorDescription}>
												{urlUploadError}
											</Typography>
										</Box>
									</Box>
								)}
							</Box>
						</Box>
					)}
				</InputLayout>

				<Divider className={styles.sectionDivider} />

				{/* Upload Dataset button*/}
				<Box className={styles.uploadDatasetSection}>
					<BriaButton
						buttonType={"primaryMedium"}
						onClick={handleUploadDataSet}
						disabled={
							!tgDatasetStore.datasetForm.name?.trim() ||
							!tgDatasetStore.datasetForm.caption_prefix?.trim() ||
							!tgDatasetStore.imagesZipUrl ||
							isUploadingDataset
						}
						loading={isUploadingDataset}
						className={styles.uploadDatasetButton}
					>
						{t("uploadDataset")}
					</BriaButton>
				</Box>

				{/* Generate Prefix Popup */}
				<ConfirmationPopup
					open={showPrefixPopup}
					onClose={() => setShowPrefixPopup(false)}
					title={t("generatePrefixPopup.title")}
					description={t("generatePrefixPopup.description")}
					hideActions={true}
					hideCloseIcon={false}
					maxWidth="600px"
				>
					<Box className={styles.prefixPopupContent}>
						<ImageUploader
							titleClassName={styles.imageUploaderTitle}
							description={t("generatePrefixPopup.uploadImages")}
							supportedFilesText={t("generatePrefixPopup.supportedFiles")}
							maxFileSize={MAX_PREFIX_IMAGES_SIZE_MB * 1024 * 1024} // 12MB
							maxFilesLimit={MAX_PREFIX_IMAGES}
							showPreview={false}
							showImageLimitText={true}
							showUploadedImagesRow={true}
							uploadedImages={prefixImages}
							showIcon={false}
							onRemoveImage={(index) => {
								setPrefixImages((prev) => prev.filter((_, i) => i !== index));
							}}
							onUpload={async (e: React.ChangeEvent<HTMLInputElement>) => {
								const files = Array.from(e.target.files || []);
								setPrefixImages((prev) => {
									const newFiles = [...prev, ...files];
									return newFiles.slice(0, MAX_PREFIX_IMAGES);
								});
							}}
							inputProps={{
								accept: "image/png, image/jpg, image/jpeg, image/webp",
								multiple: true,
							}}
						/>
						<Box className={styles.generatePrefixButtonContainer}>
							<BriaButton
								buttonType="primaryMedium"
								onClick={handleGeneratePrefix}
								loading={generatingPrefix}
								disabled={
									generatingPrefix ||
									prefixImages.length < MIN_PREFIX_IMAGES ||
									prefixImages.length > MAX_PREFIX_IMAGES
								}
								className={styles.generatePrefixButton}
							>
								{t("generatePrefixPopup.generate")}
							</BriaButton>
						</Box>
					</Box>
				</ConfirmationPopup>
			</Box>
		</LoadingPlaceholder>
	);
};

export default observer(TgAdvancedImageUploadSetup);
